import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import gjsPresetNewsletter from "grapesjs-preset-newsletter";
import gjsMJML from "grapesjs-mjml";
import { V1Layout } from "../../components/v1/V1Layout";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { epicengageSupabase } from '../../../../../integrations/supabase/client'
import { useToast } from "../../../../../hooks/use-toast";
import { useMerchant } from "../../../../../hooks/useMerchant";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../ui/dialog";
import { Send, Save, Eye, ArrowLeft } from "lucide-react";

const DEFAULT_MJML = `<mjml>
  <mj-body width="600px">
    <mj-section>
      <mj-column>
        <mj-text font-size="20px" color="#333">Start editing your email...</mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

export default function GrapesJSEditor() {
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { gtemplatid: templateId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { merchant, settings } = useMerchant();

  const [templateName, setTemplateName] = useState("Untitled Template");
  const [templateSubject, setTemplateSubject] = useState("");
  const [showTestSendDialog, setShowTestSendDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testSubject, setTestSubject] = useState("Test Email");
  const [previewHtml, setPreviewHtml] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load template from email_templates_v2
  const loadTemplate = async (id: string) => {
    try {
      const { data, error } = await epicengageSupabase
        .from("email_templates_v2")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        toast({
          title: "Template not found",
          description: "The requested template could not be found",
          variant: "destructive",
        });
        navigate("/clientpanel/enage/templates");
        return;
      }

      setTemplateName(data.name);
      setTemplateSubject(data.subject || "");

      if (editorRef.current) {
        const grapesJson = data.grapesjs_json as any;
        if (grapesJson?.mjml) {
          editorRef.current.setComponents(grapesJson.mjml);
          if (grapesJson.css) {
            editorRef.current.setStyle(grapesJson.css);
          }
        } else {
          editorRef.current.setComponents(DEFAULT_MJML);
        }
      }

      toast({
        title: "Template loaded",
        description: `Loaded "${data.name}" successfully`,
      });
    } catch (error: any) {
      console.error("Error loading template:", error);
      toast({
        title: "Error loading template",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    const editor = grapesjs.init({
      container: containerRef.current,
      plugins: [gjsMJML, gjsPresetNewsletter],
      pluginsOpts: {
        [gjsMJML as any]: {
          resetBlocks: false,
          defaultTemplate: {
            mjml: DEFAULT_MJML
          }
        },
        [gjsPresetNewsletter as any]: {},
      },
      storageManager: false,
      canvas: {
        styles: [
          `
            [data-gjs-type="mj-preview"],
            mj-preview,
            .mj-preview {
              display: none !important;
              visibility: hidden !important;
              max-height: 0 !important;
              overflow: hidden !important;
            }
            
            body {
              margin: 0;
              padding: 40px 20px;
              background: #f5f5f5;
            }
            
            .mj-body {
              max-width: 600px !important;
              margin: 0 auto !important;
              background: white;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            
            [data-gjs-type="mj-section"] {
              max-width: 600px !important;
            }
          `
        ],
        scripts: [],
      },
      assetManager: {
        upload: false,
        uploadFile: async (e: any) => {
          const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
          const file = files[0];
          
          if (!file) return;

          try {
            const { data: user } = await epicengageSupabase.auth.getUser();
            if (!user.user) {
              toast({
                title: "Error",
                description: "You must be logged in to upload images",
                variant: "destructive",
              });
              return;
            }

            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random()}.${fileExt}`;
            const filePath = `${user.user.id}/${fileName}`;

            const { error: uploadError } = await epicengageSupabase.storage
              .from('email-images')
              .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data: { publicUrl } } = epicengageSupabase.storage
              .from('email-images')
              .getPublicUrl(filePath);

            const assetManager = editor.AssetManager;
            assetManager.add([{
              src: publicUrl,
              name: file.name,
            }]);

            toast({
              title: "Success",
              description: "Image uploaded successfully",
            });
          } catch (error: any) {
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            });
          }
        },
        assets: [],
      },
      panels: {
        defaults: [
          {
            id: "views",
            buttons: [
              {
                id: "open-sm",
                className: "fa fa-paint-brush",
                command: "open-sm",
                active: true,
              },
              {
                id: "open-layers",
                className: "fa fa-bars",
                command: "open-layers",
              },
              {
                id: "open-blocks",
                className: "fa fa-th-large",
                command: "open-blocks",
              },
            ],
          },
          {
            id: "devices",
            buttons: [
              {
                id: "device-desktop",
                className: "fa fa-desktop",
                command: "set-device-desktop",
                active: true,
              },
              {
                id: "device-tablet",
                className: "fa fa-tablet",
                command: "set-device-tablet",
              },
              {
                id: "device-mobile",
                className: "fa fa-mobile",
                command: "set-device-mobile",
              },
            ],
          },
        ],
      },
    });

    // Add personalization blocks
    editor.BlockManager.add("personalization-firstname", {
      label: "First Name",
      category: "Personalization",
      content: `<mj-text>{{customer.first_name}}</mj-text>`,
    });

    editor.BlockManager.add("personalization-lastname", {
      label: "Last Name",
      category: "Personalization",
      content: `<mj-text>{{customer.last_name}}</mj-text>`,
    });

    editor.BlockManager.add("business-name", {
      label: "Business Name",
      category: "Personalization",
      content: `<mj-text>{{business_name}}</mj-text>`,
    });

    editorRef.current = editor;

    // Load template when editor is ready
    editor.on('load', () => {
      if (templateId) {
        loadTemplate(templateId);
      } else {
        editor.setComponents(DEFAULT_MJML);
      }
    });

    return () => {
      if (editorRef.current) {
        editorRef.current.destroy();
      }
    };
  }, []);

  const handleSave = async () => {
    if (!editorRef.current || !templateId || !merchant) return;

    setIsSaving(true);
    try {
      const rawMjml = editorRef.current.getHtml();
      const css = editorRef.current.getCss();

      // Compile MJML to HTML
      const { data: compiledData, error: compileError } = await epicengageSupabase.functions.invoke(
        "compile-mjml",
        { body: { mjml: rawMjml } }
      );

      if (compileError) throw compileError;

      // Update template in email_templates_v2
      const { error } = await epicengageSupabase
        .from("email_templates_v2")
        .update({
          name: templateName,
          subject: templateSubject,
          grapesjs_json: {
            mjml: rawMjml,
            css,
            components: editorRef.current.getComponents(),
          },
          html_cache: compiledData.html,
          updated_at: new Date().toISOString(),
        })
        .eq("id", templateId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Template saved successfully",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!editorRef.current) return;

    try {
      const wrapper = editorRef.current.getWrapper();
      const rawMjml = wrapper ? wrapper.toHTML() : editorRef.current.getHtml();

      const { data, error } = await epicengageSupabase.functions.invoke("compile-mjml", {
        body: { mjml: rawMjml },
      });

      if (error) throw error;

      setPreviewHtml(data.html);
      setShowPreviewDialog(true);
    } catch (error: any) {
      toast({
        title: "Error previewing email",
        description: error.message || "Failed to compile MJML",
        variant: "destructive",
      });
    }
  };

  const handleTestSend = async () => {
    if (!editorRef.current || !testEmail) return;

    const fromEmail = settings?.default_sender_email;
    if (!fromEmail) {
      toast({
        title: "No sender email configured",
        description: "Set a verified sender email in Settings before sending a test email.",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const rawMjml = editorRef.current.getHtml();

      const { error } = await epicengageSupabase.functions.invoke("send-test-email-grapesjs", {
        body: {
          fromEmail,
          to: testEmail,
          subject: testSubject,
          mjml: rawMjml,
        },
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Test email sent successfully",
      });
      setShowTestSendDialog(false);
      setTestEmail("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <V1Layout>
      <div className="flex flex-col h-[calc(100vh-0px)]">
        {/* Top Bar */}
        <div className="bg-background border-b px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/clientpanel/enage/templates")}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <Input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-48"
              placeholder="Template name"
            />
            <Input
              value={templateSubject}
              onChange={(e) => setTemplateSubject(e.target.value)}
              className="w-64"
              placeholder="Subject line"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handlePreview} variant="outline" size="sm">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
            <Button onClick={() => setShowTestSendDialog(true)} variant="outline" size="sm">
              <Send className="w-4 h-4 mr-2" />
              Test Send
            </Button>
            <Button onClick={handleSave} disabled={isSaving} size="sm">
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {/* GrapesJS Editor */}
        <div ref={containerRef} className="flex-1 w-full overflow-hidden" />

        {/* Test Send Dialog */}
        <Dialog open={showTestSendDialog} onOpenChange={setShowTestSendDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Test Email</DialogTitle>
              <DialogDescription>Enter recipient email and subject</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Subject</label>
                <Input
                  value={testSubject}
                  onChange={(e) => setTestSubject(e.target.value)}
                  placeholder="Test Email Subject"
                />
              </div>
              <div>
                <label className="text-sm font-medium">To</label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="recipient@example.com"
                />
              </div>
              <Button onClick={handleTestSend} disabled={isSending || !testEmail} className="w-full">
                {isSending ? "Sending..." : "Send Test Email"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle>Email Preview</DialogTitle>
              <DialogDescription>Preview how your email will look to recipients</DialogDescription>
            </DialogHeader>
            <div className="overflow-auto">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-[600px] border-0"
                title="Email Preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </V1Layout>
  );
}
